// safety/incidents-and-injuries/settings/SafetySettings.tsx
import React, { useState } from 'react';
import { ChevronRight, Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { AddButton } from '../components/shared';
import { todayISO, uid } from '../utils/incidents-utils';
import * as apiSvc from '../../../../utils/safety-incidents-api';
import type { SafetyLists, EmployeeRef, EntityRef, LetterTemplate } from '../../../../types/incident.types';

type SettingsTab = 'Lists' | 'Employees & Entities' | 'Letter Templates';

const LIST_DEFS: { key: keyof SafetyLists; title: string }[] = [
    { key: 'causeOfInjury', title: 'Cause of Injury' },
    { key: 'bodyPart', title: 'Body Part Injured' },
    { key: 'natureOfInjury', title: 'Nature of Injury' },
    { key: 'capacityStatus', title: 'Capacity Status' },
];

export const SafetySettings: React.FC<{
    lists: SafetyLists;
    employees: EmployeeRef[];
    entities: EntityRef[];
    templates: LetterTemplate[];
    onBack: () => void;
    onSave: (payload: { lists?: SafetyLists; employees?: EmployeeRef[]; entities?: EntityRef[]; templates?: LetterTemplate[] }) => void;
}> = ({ lists, employees, entities, templates, onBack, onSave }) => {
    const [tab, setTab] = useState<SettingsTab>('Lists');
    const [draftLists, setDraftLists] = useState<SafetyLists>(() => JSON.parse(JSON.stringify(lists)));
    const [draftTpl, setDraftTpl] = useState<LetterTemplate[]>(() => [...templates]);

    const saveLists = () => {
        const cleaned: SafetyLists = { ...draftLists };
        (Object.keys(cleaned) as (keyof SafetyLists)[]).forEach((k) => (cleaned[k] = cleaned[k].filter(Boolean)));
        onSave({ lists: cleaned });
        apiSvc.saveSettings({ lists: cleaned }).catch(() => {});
        toast.success('Lists saved');
    };
    const saveTpl = () => {
        onSave({ templates: draftTpl });
        apiSvc.saveSettings({ letterTemplates: draftTpl }).catch(() => {});
        toast.success('Templates saved');
    };

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1000px] mx-auto w-full">
            <div className="mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <button onClick={onBack} className="hover:text-foreground">Safety</button>
                    <ChevronRight size={14} />
                    <span>Settings</span>
                </div>
                <h1 className="text-2xl font-medium">Safety Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage the dropdown lists and reference data used across the Safety module.</p>
            </div>

            <div className="flex gap-1 border-b border-border mb-6">
                {(['Lists', 'Employees & Entities', 'Letter Templates'] as SettingsTab[]).map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
                            t === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {tab === 'Lists' && (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        {LIST_DEFS.map((def) => (
                            <div key={def.key} className="bg-card border border-border rounded-lg p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold">{def.title}</h3>
                                    <span className="text-xs text-muted-foreground">{draftLists[def.key].length} options</span>
                                </div>
                                <div className="space-y-2">
                                    {draftLists[def.key].map((v, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <input
                                                value={v}
                                                onChange={(e) => {
                                                    const next = { ...draftLists };
                                                    next[def.key] = [...next[def.key]];
                                                    next[def.key][i] = e.target.value;
                                                    setDraftLists(next);
                                                }}
                                                className="flex-1 h-9 rounded-lg border border-border bg-muted/40 px-3 text-sm"
                                            />
                                            <button
                                                onClick={() => {
                                                    const next = { ...draftLists };
                                                    next[def.key] = next[def.key].filter((_, j) => j !== i);
                                                    setDraftLists(next);
                                                }}
                                                className="h-9 w-9 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-destructive hover:border-destructive/30"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={() => {
                                        const next = { ...draftLists };
                                        next[def.key] = [...next[def.key], ''];
                                        setDraftLists(next);
                                    }}
                                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                                >
                                    <Plus size={16} />
                                    Add option
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4">
                        <button onClick={saveLists} className="h-11 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                            Save changes
                        </button>
                    </div>
                </>
            )}

            {tab === 'Employees & Entities' && (
                <div className="grid grid-cols-2 gap-4">
                    <RefList
                        title="Employees"
                        hint="Synced from the main system. Manage people in the People module."
                        items={employees}
                    />
                    <RefList
                        title="Employer Entities"
                        hint="Synced from the main system. Manage entities in People → Settings."
                        items={entities}
                    />
                </div>
            )}

            {tab === 'Letter Templates' && (
                <div className="bg-card border border-border rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold">Letter Templates</h3>
                        <AddButton label="Add Template" onClick={() => setDraftTpl([...draftTpl, { id: uid('tpl'), name: 'New Template', body: '', edited: todayISO(), archived: false }])} />
                    </div>
                    <div className="space-y-2">
                        {draftTpl.map((t, i) => (
                            <div key={t.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5">
                                <FileText size={16} className="text-muted-foreground" />
                                <input
                                    value={t.name}
                                    onChange={(e) => {
                                        const next = [...draftTpl];
                                        next[i] = { ...t, name: e.target.value };
                                        setDraftTpl(next);
                                    }}
                                    className="flex-1 bg-transparent text-sm border-0 p-0 focus:ring-0"
                                />
                                {t.archived && <span className="text-xs text-muted-foreground">archived</span>}
                                <button onClick={() => setDraftTpl(draftTpl.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4">
                        <button onClick={saveTpl} className="h-11 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                            Save changes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const RefList: React.FC<{
    title: string;
    hint: string;
    items: { id: string; name: string }[];
}> = ({ title, hint, items }) => (
    <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold">{title}</h3>
            <span className="text-xs text-muted-foreground">from main system</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{hint}</p>
        <div className="space-y-2">
            {items.length === 0 && <p className="text-sm text-muted-foreground">None found.</p>}
            {items.map((e) => (
                <div key={e.id} className="rounded-lg border border-border px-3 py-2 text-sm">
                    {e.name}
                </div>
            ))}
        </div>
    </div>
);
